import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { MainLayout } from '../../components/layout/MainLayout';
import { pendingOrderService, PendingOrderSummary } from '../../api/services/pendingOrderService';
import { format } from 'date-fns';
import { RefreshCw } from 'lucide-react';

export default function PendingOrders() {
  const navigate = useNavigate();
  const [includeWithComment, setIncludeWithComment] = useState(true);
  const [sortField, setSortField] = useState<keyof PendingOrderSummary>('OrderDate');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const { data: orders = [], isLoading, refetch, dataUpdatedAt } = useQuery({
    queryKey: ['pending-orders'],
    queryFn: pendingOrderService.getAll,
    refetchOnWindowFocus: false,
  });

  const handleSort = (field: keyof PendingOrderSummary) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sorted = [...orders]
    .filter(o => includeWithComment || !o.LastComment)
    .sort((a, b) => {
      const av = a[sortField];
      const bv = b[sortField];
      const cmp = typeof av === 'string' ? (av as string).localeCompare(bv as string) : (av as number) - (bv as number);
      return sortDir === 'asc' ? cmp : -cmp;
    });

  const SortTh = ({ field, label }: { field: keyof PendingOrderSummary; label: string }) => (
    <th
      onClick={() => handleSort(field)}
      className="px-4 py-3 text-left text-xs font-medium text-blue-600 dark:text-blue-400 uppercase tracking-wider cursor-pointer hover:text-blue-800 dark:hover:text-blue-200 select-none"
    >
      {label} {sortField === field ? (sortDir === 'asc' ? '↑' : '↓') : ''}
    </th>
  );

  return (
    <MainLayout>
      <div className="px-4 py-6 sm:px-0" style={{ fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif' }}>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Pending Import Orders</h2>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeWithComment}
                  onChange={e => setIncludeWithComment(e.target.checked)}
                  className="rounded border-gray-300"
                />
                Include items with comment
              </label>
              <button
                onClick={() => refetch()}
                className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600 text-sm"
              >
                <RefreshCw size={14} />
                Refresh
              </button>
            </div>
          </div>

          <div className="px-6 py-2 text-sm text-gray-500 dark:text-gray-400">
            Sorted by SO Number. Refer to original order to check for errors.
          </div>

          {isLoading ? (
            <div className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">Loading...</div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <SortTh field="OrderDate" label="Date" />
                      <SortTh field="ShipToName" label="Buyer Name" />
                      <SortTh field="CustomerName" label="Customer" />
                      <SortTh field="CustomerOrderNo" label="PO Number" />
                      <SortTh field="ItemCount" label="Items" />
                      <SortTh field="Total" label="SO Total" />
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Comment</th>
                      <th className="px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {sorted.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                          No pending orders
                        </td>
                      </tr>
                    ) : sorted.map(order => (
                      <tr key={order.Id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100 whitespace-nowrap">
                          {format(new Date(order.OrderDate), 'M/d/yy')}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{order.ShipToName}</td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{order.CustomerName}</td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100 font-mono">{order.CustomerOrderNo}</td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100 text-center">{order.ItemCount}</td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100 whitespace-nowrap">
                          ${order.Total.toFixed(2)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 max-w-xs">
                          {order.LastComment && (
                            <span>
                              {format(new Date(order.LastComment.CreateDate), 'M/d/yy h:mm a')} -{' '}
                              {order.LastComment.CreateBy} -{' '}
                              {order.LastComment.Comment}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm whitespace-nowrap">
                          <button
                            onClick={() => navigate(`/orders/pending/${order.Id}`)}
                            className="px-3 py-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-500 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-50 dark:hover:bg-gray-600 text-sm"
                          >
                            Verify
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="px-6 py-3 text-center text-sm text-gray-600 dark:text-gray-400 border-t border-gray-200 dark:border-gray-700">
                <strong>{sorted.length}</strong> unchecked as of <strong>{dataUpdatedAt ? format(new Date(dataUpdatedAt), 'M/d/yy h:mm a') : '—'}</strong>
              </div>
            </>
          )}
        </div>
      </div>
    </MainLayout>
  );
}
